import { useEffect, useMemo, useState } from 'react'

import type {
  ApplicabilityAnswer,
  ApplicabilityAnswerCode,
  ApplicabilityQuestion,
  ApplicabilityState,
} from '@/types/returnCase.types'

interface Props {
  state: ApplicabilityState
  saving: boolean
  onSave: (
    question: ApplicabilityQuestion,
    answerCode: ApplicabilityAnswerCode,
    detail: Record<string, unknown>
  ) => void
  onRecompute: () => void
  onOverride: (reason: string, citationOrEvidence: string) => void
  canOverride: boolean
  canEdit: boolean
  canRecompute: boolean
}

function valueFor(answer?: ApplicabilityAnswer): string {
  const value = answer?.detail_json.value
  return value === undefined || value === null ? '' : String(value)
}

function QuestionEditor({
  question,
  answer,
  saving,
  canEdit,
  onSave,
}: {
  question: ApplicabilityQuestion
  answer?: ApplicabilityAnswer
  saving: boolean
  canEdit: boolean
  onSave: Props['onSave']
}) {
  const [code, setCode] = useState<ApplicabilityAnswerCode>(
    answer?.answer_code ?? (question.answer_kind === 'boolean' ? 'UNSURE' : 'YES')
  )
  const [value, setValue] = useState(valueFor(answer))

  useEffect(() => {
    setCode(answer?.answer_code ?? (question.answer_kind === 'boolean' ? 'UNSURE' : 'YES'))
    setValue(valueFor(answer))
  }, [answer, question.answer_kind])

  const submit = () => {
    if (question.answer_kind === 'boolean' || code !== 'YES') {
      onSave(question, code, {})
      return
    }
    const typed =
      question.answer_kind === 'choice'
        ? value
        : question.answer_kind === 'decimal'
          ? Number.parseFloat(value)
          : Number.parseInt(value, 10)
    onSave(question, code, { value: typed })
  }

  return (
    <fieldset id={`question-${question.key}`} className="rounded-lg border border-border p-4">
      <legend className="px-1 font-medium">{question.prompt}</legend>
      <p className="mb-3 text-xs text-text-secondary">
        {question.material ? 'Required for routing' : 'Optional'} · saved independently
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex min-w-44 flex-col gap-1 text-sm">
          <span>{question.answer_kind === 'boolean' ? 'Answer' : 'Confidence'}</span>
          <select
            className="input"
            aria-label={`${question.prompt} answer`}
            value={code}
            onChange={event => setCode(event.target.value as ApplicabilityAnswerCode)}
          >
            {question.answer_kind === 'boolean' && <option value="YES">Yes</option>}
            {question.answer_kind === 'boolean' && <option value="NO">No</option>}
            {question.answer_kind !== 'boolean' && <option value="YES">I can answer</option>}
            <option value="UNSURE">Unsure</option>
            <option value="NOT_APPLICABLE">Not applicable</option>
          </select>
        </label>
        {question.answer_kind !== 'boolean' && code === 'YES' && (
          <label className="flex min-w-52 flex-1 flex-col gap-1 text-sm">
            <span>Value</span>
            {question.answer_kind === 'choice' ? (
              <select
                className="input"
                aria-label={`${question.prompt} value`}
                value={value}
                onChange={event => setValue(event.target.value)}
                required
              >
                <option value="">Select an approved option</option>
                {question.choices?.map(choice => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input"
                aria-label={`${question.prompt} value`}
                type="number"
                min="0"
                max={question.answer_kind === 'decimal' ? '1' : undefined}
                step={question.answer_kind === 'decimal' ? '0.0001' : '1'}
                value={value}
                onChange={event => setValue(event.target.value)}
                required
              />
            )}
          </label>
        )}
        <button
          className="btn btn-secondary"
          type="button"
          disabled={
            !canEdit ||
            saving ||
            (question.answer_kind !== 'boolean' && code === 'YES' && value === '')
          }
          onClick={submit}
        >
          {!canEdit ? 'Read only' : saving ? 'Saving…' : answer ? 'Update answer' : 'Save answer'}
        </button>
      </div>
      {answer && (
        <p className="mt-2 text-xs text-text-secondary">
          Version {answer.answer_version} · answered by {answer.respondent.toLowerCase()}
        </p>
      )}
    </fieldset>
  )
}

export default function ApplicabilityWorkspace({
  state,
  saving,
  onSave,
  onRecompute,
  onOverride,
  canOverride,
  canEdit,
  canRecompute,
}: Props) {
  const [step, setStep] = useState(0)
  const [overrideReason, setOverrideReason] = useState('')
  const [overrideCitation, setOverrideCitation] = useState('')
  const answers = useMemo(
    () => new Map(state.answers.map(answer => [answer.question_key, answer])),
    [state.answers]
  )
  const visible = state.questionnaire.questions.filter(question => {
    if (!question.visible_when) return true
    return answers.get(question.visible_when.question)?.answer_code === question.visible_when.answer
  })
  const pageSize = 4
  const stepCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const activeStep = Math.min(step, stepCount - 1)
  const pageQuestions = visible.slice(activeStep * pageSize, (activeStep + 1) * pageSize)
  const pendingOnStep = pageQuestions.some(
    question => question.material && !answers.has(question.key)
  )
  const routing = state.routing

  return (
    <div className="space-y-6">
      <section
        aria-labelledby="routing-summary-heading"
        className="rounded-lg border border-border p-4"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="routing-summary-heading" className="text-lg font-semibold">
              Applicability and routing
            </h2>
            <p className="text-sm text-text-secondary">
              Questionnaire {state.questionnaire.questionnaire_version} · rule pack{' '}
              {state.questionnaire.rule_pack.semantic_version}
            </p>
          </div>
          {canRecompute && (
            <button
              className="btn btn-secondary"
              type="button"
              disabled={saving}
              onClick={onRecompute}
            >
              Recompute routing
            </button>
          )}
        </div>
        {!routing && (
          <p className="mt-4 text-sm text-text-secondary">
            Save answers to generate the first governed recommendation.
          </p>
        )}
        {routing && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded border border-border p-3">
              <div className="text-xs text-text-secondary">Recommended form</div>
              <div className="text-lg font-semibold">
                {routing.effective_itr ?? 'Needs answers'}
              </div>
            </div>
            <div className="rounded border border-border p-3">
              <div className="text-xs text-text-secondary">Filing obligation</div>
              <div>{routing.filing_obligation.replace(/_/g, ' ')}</div>
            </div>
            <div className="rounded border border-border p-3">
              <div className="text-xs text-text-secondary">Due date</div>
              <div>{routing.deadline.due_date ?? routing.deadline.status.replace(/_/g, ' ')}</div>
            </div>
            <div className="rounded border border-border p-3">
              <div className="text-xs text-text-secondary">Schedules</div>
              <div>{routing.schedules.join(', ') || 'None yet'}</div>
            </div>
          </div>
        )}
        {routing?.blockers.length ? (
          <div
            className="alert alert-warning mt-4"
            role="alert"
            aria-labelledby="answer-errors-heading"
          >
            <h3 id="answer-errors-heading" className="font-semibold">
              Answers requiring attention
            </h3>
            <ul className="mt-2 list-disc pl-5">
              {routing.blockers.map((blocker, index) => (
                <li key={`${blocker.code}-${index}`}>
                  {blocker.question_key ? (
                    <button
                      className="btn btn-ghost btn-sm text-left"
                      onClick={() => {
                        const index = visible.findIndex(
                          question => question.key === blocker.question_key
                        )
                        if (index >= 0) setStep(Math.floor(index / pageSize))
                      }}
                    >
                      {blocker.message}
                    </button>
                  ) : (
                    blocker.message
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {routing?.reasons.length ? (
          <details className="mt-4">
            <summary className="cursor-pointer font-medium">Why this result?</summary>
            <ul className="mt-2 space-y-2 text-sm">
              {routing.reasons.map(reason => (
                <li key={reason.code}>
                  <span className="font-medium">{reason.code}</span> — {reason.message}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {canOverride && routing?.recommended_itr === 'ITR-4' && !routing.override ? (
          <details className="mt-4 rounded border border-border p-3">
            <summary className="cursor-pointer font-medium">
              Professional conservative-form override
            </summary>
            <p className="mt-2 text-sm text-text-secondary">
              Authorized Firm Owners and CA Partners may select ITR-3 instead. The original
              recommendation remains in history.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span>Professional reason</span>
                <textarea
                  className="input min-h-24"
                  value={overrideReason}
                  onChange={event => setOverrideReason(event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>Citation or evidence reference</span>
                <textarea
                  className="input min-h-24"
                  value={overrideCitation}
                  onChange={event => setOverrideCitation(event.target.value)}
                />
              </label>
            </div>
            <button
              className="btn btn-secondary mt-3"
              type="button"
              disabled={saving || overrideReason.length < 10 || overrideCitation.length < 3}
              onClick={() => onOverride(overrideReason, overrideCitation)}
            >
              Override to ITR-3
            </button>
          </details>
        ) : null}
        {routing?.override ? (
          <div className="alert alert-warning mt-4">
            Professional override applied. Effective form: {routing.effective_itr}; governed
            recommendation retained: {routing.recommended_itr}.
          </div>
        ) : null}
      </section>

      <section aria-labelledby="questions-heading">
        <h2 id="questions-heading" className="mb-3 text-lg font-semibold">
          Questions
        </h2>
        <nav aria-label="Assessment questionnaire steps" className="mb-4 flex flex-wrap gap-2">
          {Array.from({ length: stepCount }, (_, index) => (
            <button
              type="button"
              key={index}
              className={
                index === activeStep
                  ? 'btn btn-outline btn-sm text-primary-600'
                  : 'btn btn-ghost btn-sm'
              }
              aria-current={index === activeStep ? 'step' : undefined}
              onClick={() => setStep(index)}
            >
              Step {index + 1}
            </button>
          ))}
        </nav>
        <p className="mb-3 text-sm text-text-secondary">
          Step {activeStep + 1} of {stepCount} · {state.answers.length} saved answers. Answers are
          saved as you go.
        </p>
        <div className="space-y-3">
          {pageQuestions.map(question => (
            <QuestionEditor
              key={question.key}
              question={question}
              answer={answers.get(question.key)}
              saving={saving}
              canEdit={canEdit}
              onSave={onSave}
            />
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={activeStep === 0 || saving}
            onClick={() => setStep(activeStep - 1)}
          >
            Back
          </button>
          {activeStep < stepCount - 1 ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || pendingOnStep}
              onClick={() => setStep(activeStep + 1)}
            >
              Continue
            </button>
          ) : (
            <span className="text-sm text-text-secondary">
              Review the routing summary and any answers requiring attention.
            </span>
          )}
        </div>
        {pendingOnStep && (
          <p className="mt-2 text-sm text-text-secondary">
            Save each required answer on this step to continue. Choose Unsure when you need help.
          </p>
        )}
      </section>

      {routing?.checklist.length ? (
        <section aria-labelledby="checklist-heading">
          <h2 id="checklist-heading" className="mb-3 text-lg font-semibold">
            Generated checklist
          </h2>
          <ul className="space-y-2">
            {routing.checklist.map(slot => (
              <li key={slot.slot_key} className="rounded border border-border p-3">
                <span className="badge">{slot.kind}</span>{' '}
                <span className="ml-2">{slot.label}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
